import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
  creditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import {
  UserDropdownInfiniteScrollProps,
  userEmailOption,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useDropdownInfiniteScroll } from 'src/hooks/useDropdownInfiniteScroll'
import {
  CreditCardListModel,
  CreditCardStatus,
  CreditCardType,
} from 'src/models/creditCard.models'
import { CreditCardPermissions } from 'src/models/creditCardPermissions.models'
import { UserAssociation, UserListModel } from 'src/models/user.models'
import { creditCardListQueries } from 'src/queries/creditCards/creditCardList.queries'
import { userSearchQueries } from 'src/queries/users/userSearch.queries'

interface BudgetRequestOptions {
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  rcnOptions: CreditCardOption[]
  recentUserOptions: UserOption[]
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  userOptions: UserOption[]
}

const sourceAccountFilters = {
  budgetsEnabled: true,
  count: 10,
  sortDirection: 'ASC',
  sortField: 'type',
  status: CreditCardStatus.Active,
  type: CreditCardType.Source,
  withPermission: CreditCardPermissions.IssueDelegateCreditCards,
}

const uniqueUserModelsByEmail = (
  users: Array<UserListModel | undefined>,
): UserListModel[] => {
  const seenEmails = new Set<string>()

  return users.filter((user): user is UserListModel => {
    if (!user || seenEmails.has(user.email)) return false
    seenEmails.add(user.email)
    return true
  })
}

export const useBudgetRequestOptions = (): BudgetRequestOptions => {
  const currentUser = useCurrentUser()
  const [accountSearch, setAccountSearch] = useState('')
  const [userSearch, setUserSearch] = useState('')

  const accountQuery = useInfiniteQuery(
    creditCardListQueries.list(currentUser?.id, {
      ...sourceAccountFilters,
      search: accountSearch,
    }),
  )
  const userQuery = useInfiniteQuery(
    userSearchQueries.list(currentUser?.id, { count: 10, q: userSearch }),
  )
  const recentUsersQuery = useQuery({
    ...userSearchQueries.recent(
      currentUser?.id,
      UserAssociation.delegateCreditCardTo,
    ),
    enabled: Boolean(currentUser?.id && currentUser.hasOrgAdminPermissions),
  })
  const showSuggestedUsers = Boolean(currentUser?.hasOrgAdminPermissions)

  const rcnOptions = useMemo(
    () =>
      accountQuery.data?.pages.flatMap((page) =>
        page.creditCards.flatMap((creditCard) => {
          const model = new CreditCardListModel(creditCard)
          return model.isBudget ? [] : [creditCardOption(model)]
        }),
      ) ?? [],
    [accountQuery.data],
  )

  const recentUsers = useMemo(
    () =>
      showSuggestedUsers
        ? uniqueUserModelsByEmail([
            currentUser,
            ...(recentUsersQuery.data?.users.map(
              (user) => new UserListModel(user),
            ) ?? []),
          ]).slice(0, 5)
        : [],
    [currentUser, recentUsersQuery.data, showSuggestedUsers],
  )
  const recentUserOptions = useMemo(
    () => recentUsers.map(userEmailOption),
    [recentUsers],
  )
  const recentUserEmails = useMemo(
    () => new Set(recentUsers.map((user) => user.email)),
    [recentUsers],
  )
  const userOptions = useMemo(() => {
    const searchedUsers =
      userQuery.data?.pages.flatMap((page) =>
        page.users.map((user) => new UserListModel(user)),
      ) ?? []
    const availableUsers = uniqueUserModelsByEmail([
      ...(showSuggestedUsers ? [] : [currentUser]),
      ...searchedUsers,
    ])

    return availableUsers.flatMap((user) =>
      recentUserEmails.has(user.email) ? [] : [userEmailOption(user)],
    )
  }, [currentUser, recentUserEmails, showSuggestedUsers, userQuery.data])

  const accountPage = accountQuery.data?.pages.at(-1)?.pagination.page ?? 0
  const userPage = userQuery.data?.pages.at(-1)?.pagination.page ?? 0

  const creditCardInfiniteScrollProps = useDropdownInfiniteScroll({
    currentPage: accountPage,
    currentSearch: accountSearch,
    fetchNextPage: accountQuery.fetchNextPage,
    isComplete: !accountQuery.hasNextPage,
    isFetchingNextPage: accountQuery.isFetchingNextPage,
    isLoading: accountQuery.isLoading,
    setSearch: setAccountSearch,
  })
  const userInfiniteScrollProps = useDropdownInfiniteScroll({
    currentPage: userPage,
    currentSearch: userSearch,
    fetchNextPage: userQuery.fetchNextPage,
    isComplete: !userQuery.hasNextPage,
    isFetchingNextPage: userQuery.isFetchingNextPage,
    isLoading: userQuery.isLoading,
    setSearch: setUserSearch,
  })

  return {
    creditCardInfiniteScrollProps,
    rcnOptions,
    recentUserOptions,
    userInfiniteScrollProps,
    userOptions,
  }
}
